/**
 * Shared DDX (Differential Diagnosis) Utilities
 *
 * Pure helper functions used by both ChartMind and SymptomChecker DDX hooks.
 * These handle the common "old format vs new format" data shape and condition
 * name normalization for comparison.
 */

/**
 * Extract all diagnoses from a DDX data object, reading every possible array
 * (`diagnoses`, `primary_diagnoses`, `alternative_diagnoses`) and deduplicating
 * by condition name.  This mirrors how the D3 visualization collects diagnoses
 * via `transformDiagnosisToGraph`, so every consumer sees the same complete set.
 *
 * @param {Object|null} ddxData - The DDX data object from LLM or storage.
 * @returns {Array} Flat, deduplicated array of diagnosis objects (shallow copies).
 */
export function getAllDiagnoses(ddxData) {
  if (!ddxData) return [];

  const seen = new Set();
  const out = [];
  for (const arr of [ddxData.diagnoses, ddxData.primary_diagnoses, ddxData.alternative_diagnoses]) {
    if (!Array.isArray(arr)) continue;
    for (const d of arr) {
      if (!d) continue;
      const name = extractDiagnosisName(d).toLowerCase().trim();
      if (name && !seen.has(name)) {
        seen.add(name);
        out.push(d);
      }
    }
  }
  return out;
}

/**
 * Extract the raw condition name string from a diagnosis object.
 *
 * Handles various LLM response shapes:
 *   - `{ condition: "..." }`
 *   - `{ name: "..." }`
 *   - `{ title: "..." }`
 *   - `{ diagnosis: "..." }`
 *   - plain string
 *
 * @param {Object|string} diagnosis - A diagnosis entry (object or string).
 * @returns {string} The raw condition name, or empty string if extraction fails.
 */
export function extractDiagnosisName(diagnosis) {
  if (!diagnosis) return '';

  if (typeof diagnosis === 'string') return diagnosis;

  if (typeof diagnosis === 'object') {
    // Try property names in order of prevalence
    for (const key of ['condition', 'name', 'title', 'diagnosis', 'label']) {
      if (typeof diagnosis[key] === 'string' && diagnosis[key].trim()) {
        return diagnosis[key];
      }
    }
  }

  return '';
}

/**
 * Normalize a diagnosis condition name for comparison / deduplication.
 *
 * Returns a lowercase, trimmed string suitable for Map keys or equality checks.
 * Handles both diagnosis objects and plain strings.
 *
 * @param {Object|string} diagnosis - A diagnosis entry (object or string).
 * @returns {string} Normalized key (lowercase, trimmed). Empty string on failure.
 */
export function normalizeConditionKey(diagnosis) {
  return extractDiagnosisName(diagnosis).toLowerCase().trim();
}

/**
 * Merge two diagnosis arrays using an "add-only, never-remove" strategy.
 *
 * Core algorithm shared by ChartMind and SymptomChecker DDX hooks.
 * Walks the incoming array: if a diagnosis already exists (by normalized key),
 * it is updated via `mergeEntry`; otherwise it is appended.
 *
 * @param {Array}  existing  - Current diagnosis array.
 * @param {Array}  incoming  - New diagnosis array from LLM.
 * @param {Object} [options]
 * @param {Function} [options.normalizeKey] - `(dx) => string` key for comparison.
 *   Defaults to `normalizeConditionKey`.
 * @param {Function} [options.mergeEntry]  - `(existingItem, incomingItem) => mergedItem`.
 *   Defines how matched entries are combined.  Defaults to spreading incoming over existing.
 * @param {boolean}  [options.deduplicateExisting=false] - Remove duplicates in `existing`
 *   before merging (SymptomChecker needs this; ChartMind does not).
 * @param {boolean}  [options.deduplicateIncoming=false] - Skip duplicate keys within
 *   the incoming array when adding new entries.
 * @returns {Array} Merged diagnosis array (new reference).
 */
export function mergeDiagnosisArrays(existing = [], incoming = [], options = {}) {
  const {
    normalizeKey = normalizeConditionKey,
    mergeEntry = (existingItem, incomingItem) => ({ ...existingItem, ...incomingItem }),
    deduplicateExisting = false,
    deduplicateIncoming = false,
  } = options;

  // Step 1: Optionally deduplicate existing array (keep first occurrence)
  let deduped = existing;
  if (deduplicateExisting) {
    const seen = new Set();
    deduped = existing.filter(d => {
      const key = normalizeKey(d);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Step 2: Build lookup of existing keys for O(1) membership tests
  const existingKeys = new Set(deduped.map(d => normalizeKey(d)));

  // Step 3: Update existing entries that have a match in incoming
  const incomingMap = new Map();
  for (const inc of incoming) {
    const key = normalizeKey(inc);
    if (key && !incomingMap.has(key)) {
      incomingMap.set(key, inc);
    }
  }

  const updatedExisting = deduped.map(existingItem => {
    const key = normalizeKey(existingItem);
    const incomingItem = incomingMap.get(key);
    return incomingItem ? mergeEntry(existingItem, incomingItem) : existingItem;
  });

  // Step 4: Collect genuinely new items (not already in existing)
  const seenNew = new Set();
  const newItems = incoming.filter(d => {
    const key = normalizeKey(d);
    if (!key) return false;
    if (existingKeys.has(key)) return false;
    if (deduplicateIncoming && seenNew.has(key)) return false;
    seenNew.add(key);
    return true;
  });

  return [...updatedExisting, ...newItems];
}
